const DESKTOP_BREAKPOINT = 992;
const SCROLL_CORRECTION_TOLERANCE_PX = 2;
const SMOOTH_SCROLL_CORRECTION_DELAY_MS = 350;
const ACTIVE_LETTER_CLASS = 'word-list-block__letters-item--active';
const INITIALIZED_ATTRIBUTE = 'data-word-list-block-initialized';

const useWordListBlock = (blockElement: HTMLElement): void => {
  if (blockElement.hasAttribute(INITIALIZED_ATTRIBUTE)) {
    return;
  }

  const pageHeader =
    document.getElementById('page-header') ?? document.querySelector<HTMLElement>('.page-header');
  const lettersNav = blockElement.querySelector<HTMLElement>('.word-list-block__letters');
  const lettersEllipsis = blockElement.querySelector<HTMLElement>('.word-list-block__letters-ellipsis');
  const toggleButton = blockElement.querySelector<HTMLButtonElement>('.word-list-block__letters-toggle');
  const letterLinks = Array.from(
    blockElement.querySelectorAll<HTMLAnchorElement>('.word-list-block__letters-link[data-anchor-id]'),
  );
  const letterItems = Array.from(
    blockElement.querySelectorAll<HTMLElement>('.word-list-block__letters-item'),
  );
  const letterHeadings = Array.from(
    blockElement.querySelectorAll<HTMLElement>('.word-list-block__letter-row-heading[id]'),
  );

  if (!lettersNav || !toggleButton || letterItems.length === 0) {
    return;
  }

  blockElement.setAttribute(INITIALIZED_ATTRIBUTE, 'true');
  blockElement.classList.add('word-list-block--is-enhanced');

  const openLabel = lettersNav.dataset.openLabel ?? '';
  const closeLabel = lettersNav.dataset.closeLabel ?? '';
  const isDesktop = (): boolean => window.innerWidth >= DESKTOP_BREAKPOINT;
  let pageHeaderBottom = 0;
  const letterLinksByAnchorId = new Map<string, HTMLAnchorElement>();
  const isMobileOpen = (): boolean => lettersNav.classList.contains('word-list-block__letters--is-open');

  letterLinks.forEach((link) => {
    const anchorId = link.dataset.anchorId;
    if (anchorId) {
      letterLinksByAnchorId.set(anchorId, link);
    }
  });

  const resolveCssLengthToPx = (value: string): number => {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return 0;
    }

    const parsedValue = parseFloat(trimmedValue);
    if (Number.isFinite(parsedValue) && /^-?[\d.]+px$/.test(trimmedValue)) {
      return parsedValue;
    }

    const probe = document.createElement('div');
    probe.style.position = 'absolute';
    probe.style.visibility = 'hidden';
    probe.style.pointerEvents = 'none';
    probe.style.width = trimmedValue;
    blockElement.appendChild(probe);
    const resolvedWidth = probe.getBoundingClientRect().width;
    probe.remove();

    return Number.isFinite(resolvedWidth) ? resolvedWidth : 0;
  };

  const getStickyOffsetPx = (): number => {
    const blockStyles = window.getComputedStyle(blockElement);
    const stickyTop = resolveCssLengthToPx(blockStyles.getPropertyValue('--word-list-sticky-top'));
    const paddingTop = resolveCssLengthToPx(blockStyles.getPropertyValue('--word-list-padding-top'));
    const toolbarHeight = resolveCssLengthToPx(blockStyles.getPropertyValue('--word-list-toolbar-height'));
    const spacingSmall = resolveCssLengthToPx(blockStyles.getPropertyValue('--spacing-small'));

    return stickyTop + paddingTop + toolbarHeight + spacingSmall;
  };

  const getAnchorTargetY = (targetHeading: HTMLElement): number => {
    const targetTopInDocument = targetHeading.getBoundingClientRect().top + window.scrollY;
    const rawTargetY = targetTopInDocument - getStickyOffsetPx();
    const maxScrollY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);

    return Math.max(0, Math.min(rawTargetY, maxScrollY));
  };

  const setActiveAnchor = (anchorId: string | null): void => {
    letterItems.forEach((item) => {
      item.classList.remove(ACTIVE_LETTER_CLASS);
    });
    letterLinks.forEach((link) => link.removeAttribute('aria-current'));

    if (!anchorId) {
      return;
    }

    const activeLink = letterLinksByAnchorId.get(anchorId);
    const activeItem = activeLink?.closest<HTMLElement>('.word-list-block__letters-item');

    activeItem?.classList.add(ACTIVE_LETTER_CLASS);
    activeLink?.setAttribute('aria-current', 'location');
  };

  const getCurrentAnchorIdFromScroll = (): string | null => {
    if (letterHeadings.length === 0) {
      return null;
    }

    const thresholdY = window.scrollY + getStickyOffsetPx() + 1;
    let currentAnchorId: string | null = null;

    letterHeadings.forEach((heading) => {
      const headingTop = heading.getBoundingClientRect().top + window.scrollY;
      if (headingTop <= thresholdY) {
        currentAnchorId = heading.id;
      }
    });

    return currentAnchorId ?? letterHeadings[0]?.id ?? null;
  };

  const updateActiveAnchorFromScroll = (): void => {
    setActiveAnchor(getCurrentAnchorIdFromScroll());
  };

  const applyScrollToAnchor = (targetHeading: HTMLElement, anchorId: string): Promise<void> => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const scrollBehavior: ScrollBehavior = prefersReducedMotion ? 'auto' : 'smooth';

    window.scrollTo({ top: getAnchorTargetY(targetHeading), behavior: scrollBehavior });
    history.replaceState(null, '', `#${anchorId}`);

    const correctionDelay = prefersReducedMotion ? 0 : SMOOTH_SCROLL_CORRECTION_DELAY_MS;
    return new Promise((resolve) => {
      window.setTimeout(() => {
        updatePageHeaderBottom();
        updatePaddingTopState();
        syncActiveAnchorState();

        const correctedTargetY = getAnchorTargetY(targetHeading);
        if (Math.abs(window.scrollY - correctedTargetY) > SCROLL_CORRECTION_TOLERANCE_PX) {
          window.scrollTo({ top: correctedTargetY, behavior: 'auto' });
          syncActiveAnchorState();
        }

        resolve();
      }, correctionDelay);
    });
  };

  const updatePageHeaderBottom = (): void => {
    if (!pageHeader) {
      pageHeaderBottom = 0;
      return;
    }

    const rect = pageHeader.getBoundingClientRect();
    pageHeaderBottom = rect.bottom + window.scrollY;
  };

  const updatePaddingTopState = (): void => {
    if (!pageHeader) {
      blockElement.classList.remove('word-list-block--past-page-header');
      return;
    }

    blockElement.classList.toggle('word-list-block--past-page-header', window.scrollY > pageHeaderBottom);
  };

  const setToggleState = (isOpen: boolean): void => {
    if (isDesktop()) {
      lettersNav.classList.remove('word-list-block__letters--is-open');
      toggleButton.setAttribute('aria-expanded', 'false');
      toggleButton.setAttribute('aria-label', openLabel);
      return;
    }

    lettersNav.classList.toggle('word-list-block__letters--is-open', isOpen);
    toggleButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    toggleButton.setAttribute('aria-label', isOpen ? closeLabel : openLabel);
  };

  const clearVisibleWindow = (): void => {
    letterItems.forEach((item) => item.classList.remove('word-list-block__letters-item--visible'));
    lettersEllipsis?.classList.remove('word-list-block__letters-ellipsis--visible');
  };

  const getActiveItemIndex = (): number =>
    letterItems.findIndex((item) => item.classList.contains(ACTIVE_LETTER_CLASS));

  const getClosedVisibleCount = (): number => {
    const lettersList = blockElement.querySelector<HTMLElement>('.word-list-block__letters-list');
    if (!lettersList) {
      return letterItems.length;
    }

    const firstLink = lettersList.querySelector<HTMLElement>('.word-list-block__letters-link');
    if (!firstLink) {
      return letterItems.length;
    }

    const listWidth = lettersList.clientWidth;
    const listStyles = window.getComputedStyle(lettersList);
    const gap = parseFloat(listStyles.columnGap || listStyles.gap || '0') || 0;

    const linkStyles = window.getComputedStyle(firstLink);
    const minWidth = parseFloat(linkStyles.minWidth || '0') || 0;
    const measuredWidth = firstLink.getBoundingClientRect().width;
    const itemWidth = Math.max(minWidth, measuredWidth);
    const itemWidthWithGap = itemWidth + gap;

    if (listWidth <= 0 || itemWidthWithGap <= 0) {
      return letterItems.length;
    }

    const fitCount = Math.floor((listWidth + gap) / itemWidthWithGap);
    return Math.max(1, Math.min(letterItems.length, fitCount));
  };

  const applyClosedVisibleWindow = (): void => {
    if (isDesktop() || isMobileOpen()) {
      clearVisibleWindow();
      return;
    }

    clearVisibleWindow();
    const fitCount = getClosedVisibleCount();
    const hasOverflow = letterItems.length > fitCount;
    const visibleCount = hasOverflow ? Math.max(1, fitCount - 1) : fitCount;
    const activeItemIndex = getActiveItemIndex();
    const startIndex = activeItemIndex >= 0 ? Math.max(0, activeItemIndex - visibleCount + 1) : 0;

    for (let index = startIndex; index < startIndex + visibleCount; index += 1) {
      letterItems[index]?.classList.add('word-list-block__letters-item--visible');
    }

    if (hasOverflow) {
      lettersEllipsis?.classList.add('word-list-block__letters-ellipsis--visible');
    }
  };

  const syncActiveAnchorState = (): void => {
    updateActiveAnchorFromScroll();

    if (!isDesktop() && !isMobileOpen()) {
      applyClosedVisibleWindow();
    }
  };

  const onViewportUpdate = (): void => {
    updatePageHeaderBottom();
    updatePaddingTopState();
    syncActiveAnchorState();

    if (isDesktop()) {
      setToggleState(false);
      clearVisibleWindow();
      return;
    }

    if (!isMobileOpen()) {
      applyClosedVisibleWindow();
    }
  };

  const onWindowScroll = (): void => {
    updatePaddingTopState();
    syncActiveAnchorState();
  };

  toggleButton.addEventListener('click', (event) => {
    if (isDesktop()) {
      return;
    }

    event.stopImmediatePropagation();
    setToggleState(!isMobileOpen());
    applyClosedVisibleWindow();
  });

  letterLinks.forEach((link) => {
    link.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const anchorId = link.dataset.anchorId;
      if (!anchorId) {
        return;
      }

      const targetHeading = document.getElementById(anchorId);
      if (!targetHeading) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      if (!isDesktop()) {
        setToggleState(false);
      }

      setActiveAnchor(anchorId);

      if (!isDesktop()) {
        applyClosedVisibleWindow();
      }

      updatePageHeaderBottom();
      updatePaddingTopState();
      applyScrollToAnchor(targetHeading, anchorId).then(() => {
        // Set focus to the heading for screen readers to read
        (targetHeading.querySelector("span:first-child") as HTMLSpanElement).focus({ preventScroll: true });
      });
    });
  });

  onViewportUpdate();
  window.addEventListener('scroll', onWindowScroll, { passive: true });
  window.addEventListener('resize', onViewportUpdate);
};

const wordListBlocks = Array.from(document.querySelectorAll<HTMLElement>('.word-list-block'));
wordListBlocks.forEach((blockElement) => {
  useWordListBlock(blockElement);
});
